/**
 * useCartManagement Composable
 * Handles all cart-related operations (Single Responsibility Principle)
 * Compatible with Vue 2 and Vue 3
 */

/**
 * Composable for managing cart operations
 * @param {Object} store - Vuex store instance
 * @param {Object} router - Vue router instance
 * @returns {Object} Cart management methods and state
 */
export function useCartManagement(store, router) {
  if (!store) {
    console.warn("useCartManagement: Store instance is required");
    return {
      addToCart: () => {},
      updateQuantity: () => {},
      removeItem: () => {},
      checkout: () => {},
      setCartOpen: () => {},
      setCartPinned: () => {}
    };
  }

  /**
   * Adds item to cart
   * @param {Object} params - Item data
   * @param {Object} params.item - Product item
   * @param {number} params.quantity - Quantity
   * @param {string} params.notes - Notes
   * @param {Array} params.modifiers - Selected modifiers
   */
  function addToCart({ item, quantity, notes, modifiers }) {
    return store.dispatch("addToCart", {
      item,
      quantity,
      note: notes,
      modifiers
    });
  }

  /**
   * Updates item quantity in cart
   * @param {string} cartId - Cart item ID
   * @param {number} delta - Quantity change (+1 or -1)
   */
  function updateQuantity(cartId, delta) {
    return store.dispatch("updateQuantity", { cartId, delta });
  }

  /**
   * Removes item from cart
   * @param {string} cartId - Cart item ID
   */
  function removeItem(cartId) {
    return store.dispatch("removeItemCart", cartId);
  }

  /**
   * Updates an existing cart item (edit flow)
   * @param {string} cartId - Cart item ID to replace
   * @param {Object} params - Same shape as addToCart
   */
  function updateCartItem(cartId, { item, quantity, notes, modifiers }) {
    return store.dispatch("updateCartItem", {
      cartId,
      item,
      quantity,
      note: notes,
      modifiers
    });
  }

  /**
   * Handles checkout navigation
   * @param {string} storeAlias - Store alias for route
   */
  function checkout(storeAlias) {
    if (storeAlias && router) {
      router.push({
        name: "Checkout",
        params: { storeAlias }
      });
    }
    store.commit("SET_CART_OPEN", false);
  }

  /**
   * Opens or closes cart drawer
   * @param {boolean} isOpen - Whether cart should be open
   */
  function setCartOpen(isOpen) {
    store.commit("SET_CART_OPEN", isOpen);
  }

  /**
   * Pins or unpins cart sidebar
   * @param {boolean} isPinned - Whether cart should be pinned
   * @param {Array} cartItems - Current cart items (to close drawer if pinning)
   */
  function setCartPinned(isPinned, cartItems = []) {
    store.commit("SET_CART_PINNED", isPinned);
    // If pinning and there are items, ensure drawer closes
    if (isPinned && cartItems.length > 0) {
      store.commit("SET_CART_OPEN", false);
    }
  }

  return {
    addToCart,
    updateQuantity,
    removeItem,
    updateCartItem,
    checkout,
    setCartOpen,
    setCartPinned
  };
}
